import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Save, Send, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QuestionRenderer } from "@/components/lift/QuestionRenderer";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function PeerResponseForm() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [strengths, setStrengths] = useState("");
  const [growth, setGrowth] = useState("");
  const [saving, setSaving] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/lift/peer-tasks/${requestId}`);
      setState(data);
      setAnswers(data.response?.answers || []);
      setStrengths(data.response?.strengths_text || "");
      setGrowth(data.response?.growth_text || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      await api.put(`/lift/peer-tasks/${requestId}/response`, {
        answers,
        strengths_text: strengths,
        growth_text: growth,
        status: nextStatus,
      });
      toast.success(nextStatus === "submitted" ? "Feedback shared. Thank you." : "Draft saved.");
      if (nextStatus === "submitted") navigate("/lift/peer-tasks");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitDecline = async () => {
    if (declineReason.trim().length < 3) {
      toast.error("Please share a brief reason.");
      return;
    }
    try {
      await api.post(`/lift/peer-tasks/${requestId}/decline`, { reason: declineReason });
      toast.success("Politely declined.");
      navigate("/lift/peer-tasks");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  if (!state) return <p className="italic text-charcoal-muted">Loading…</p>;
  const submitted = state.response?.status === "submitted";

  return (
    <div className="space-y-6" data-testid="lift-peer-response">
      <Link to="/lift/peer-tasks" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> My peer tasks
      </Link>

      <div>
        <ModuleTitleLIFT />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">
          Peer Feedback For {state.request.subject_name}
        </h1>
        <p className="text-sm text-charcoal-muted">
          {state.request.cycle_name}
          {state.request.due_date ? ` · due ${state.request.due_date}` : ""}
        </p>
        {submitted && (
          <p className="mt-2 inline-block patria-chip-green">Submitted — thank you</p>
        )}
      </div>

      <div className="patria-card p-6 sm:p-8 space-y-6">
        <QuestionRenderer
          template={state.template}
          answers={answers}
          onChange={setAnswers}
          disabled={submitted || saving}
        />

        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
            Strengths — what you regularly see
          </label>
          <textarea
            className="patria-input w-full min-h-[100px]"
            value={strengths}
            onChange={(e) => setStrengths(e.target.value)}
            disabled={submitted || saving}
            data-testid="peer-strengths"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1">
            Growth invitation — one kind, honest offering
          </label>
          <textarea
            className="patria-input w-full min-h-[100px]"
            value={growth}
            onChange={(e) => setGrowth(e.target.value)}
            disabled={submitted || saving}
            data-testid="peer-growth"
          />
        </div>
      </div>

      {!submitted && (
        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <button
            className="patria-btn-primary"
            onClick={() => save("submitted")}
            disabled={saving}
            data-testid="peer-submit-response"
          >
            <Send className="h-4 w-4" strokeWidth={1.5} /> Share feedback
          </button>
          <button
            className="patria-btn-secondary"
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="peer-save-draft"
          >
            <Save className="h-4 w-4" strokeWidth={1.5} /> Save draft
          </button>
          <button
            className="patria-btn-ghost text-terracotta-600 sm:mr-auto"
            onClick={() => setShowDecline(true)}
            data-testid="peer-decline-open"
          >
            <XCircle className="h-4 w-4" strokeWidth={1.5} /> Politely decline
          </button>
        </div>
      )}

      {showDecline && (
        <div className="fixed inset-0 z-50 bg-forest-900/40 flex items-center justify-center p-4" data-testid="peer-decline-modal">
          <div className="patria-card w-full max-w-md p-6">
            <h3 className="text-2xl font-serif text-forest-900">Decline This Request</h3>
            <p className="mt-2 text-sm text-charcoal-soft">
              A short reason helps People Operations follow up warmly. It won&rsquo;t be shown to the employee.
            </p>
            <textarea
              className="patria-input w-full min-h-[90px] mt-3"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              data-testid="peer-decline-reason"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button className="patria-btn-secondary" onClick={() => setShowDecline(false)}>
                Never mind
              </button>
              <button className="patria-btn-terracotta" onClick={submitDecline} data-testid="peer-decline-confirm">
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
